/**
 * SOLID Review
 * S — Strong. One concern: which service answers each context's decisions — chosen once, here.
 * O — Good. A new context is one field, one construction and one entry in `tables()`.
 * L — Strong. Implements `IConfigDepClientServices` exactly; no inheritance.
 * I — Strong. One interface, one class.
 * D — Good. Names the concrete services — this is where they are chosen.
 */
import { TablePlay } from './table/TablePlay';
import { TableSync } from './table/TableSync';
import { Session } from './session/Session';
import { Seating } from './session/Seating';
import { Legal } from './legal/Legal';
import { Media } from './media/Media';
import { Payment } from './payment/Payment';
import { Ads } from './ads/Ads';
import { Diagnostics } from './diagnostics/Diagnostics';
import type { TClientOp, TDecisionRows } from './IServiceDecisionTable';

/** The client Domain's context services: each offers its ops' table, and the Facade merges them. */
export interface IConfigDepClientServices {
  readonly play: TablePlay;
  readonly sync: TableSync;
  readonly session: Session;
  readonly seating: Seating;
  readonly legal: Legal;
  readonly media: Media;
  readonly payment: Payment;
  readonly ads: Ads;
  readonly diagnostics: Diagnostics;
  /** The services' decision tables, as the Facade counts who answers each op. */
  tables(): ReadonlyArray<Partial<TDecisionRows<TClientOp>>>;
}

/**
 * THE DOMAIN'S SERVICES, BUILT ONCE (frontend-domain-oo P5, 2026-10-26; handbook §28.272) — the backend's
 * `DomainServices` (domain-oo P7, §28.221), without policies: a client rule chooses no strategy. `ComponentFrontendDomain`
 * builds them and hands them to the Facade; wiring inside the box is constructor injection, not ports. The small
 * contexts' five (legal, media, payment, ads, diagnostics) since P6 (§28.273), where an interim `SmallContexts` table
 * of their decide functions stood.
 */
export class ClientServices implements IConfigDepClientServices {
  readonly play = new TablePlay();
  readonly sync = new TableSync();
  readonly session = new Session();
  readonly seating = new Seating();
  readonly legal = new Legal();
  readonly media = new Media();
  readonly payment = new Payment();
  readonly ads = new Ads();
  readonly diagnostics = new Diagnostics();

  tables(): ReadonlyArray<Partial<TDecisionRows<TClientOp>>> {
    return [
      this.play.decisions, this.sync.decisions, this.session.decisions, this.seating.decisions, this.legal.decisions,
      this.media.decisions, this.payment.decisions, this.ads.decisions, this.diagnostics.decisions,
    ];
  }
}
